/**
 * Composable per Orders By Allowed Worker autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { OrdersByAllowedWorkerOptions } from '@/lib/autocompletes';
import { getOrdersByAllowedWorkerAutocomplete } from '@/lib/autocompletes/ordersByAllowedWorker';
import { JobOrderAutocompleteResult } from '~/lib/models/jobOrder';

export function useOrdersByAllowedWorkerAutocomplete(
	options: BaseAutocompleteComposableOptions = {},
) {
	const ordersByAllowedWorker = ref<JobOrderAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchOrdersByAllowedWorker(
		options: OrdersByAllowedWorkerOptions = {},
	): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getOrdersByAllowedWorkerAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					ordersByAllowedWorker.value = result;
				}
			} catch (err) {
				console.error(
					'❌ ERROR useOrdersByAllowedWorkerAutocomplete.searchOrdersByAllowedWorker:',
					err,
				);
				error(
					'Errore durante il caricamento delle commesse per lavoratore! Ricaricare la pagina e riprovare.',
				);
				if (token.isLatest()) {
					ordersByAllowedWorker.value = [];
				}
			}
		}, 'searchOrdersByAllowedWorker');
	}

	function clearOrdersByAllowedWorker(): void {
		ordersByAllowedWorker.value = [];
	}

	return {
		ordersByAllowedWorker: readonly(ordersByAllowedWorker),
		searchOrdersByAllowedWorker,
		clearOrdersByAllowedWorker,
	};
}
